import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatMarkdownComponents } from './utils/chatTable';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getAuthHeaders, getUser } from './auth';
import { alignNumericColumns } from './utils/markdownTables';
import { newConversationId } from './activity/track';
import useSpeechToText from './hooks/useSpeechToText';
import EfdWelcomeLoader from './EfdWelcomeLoader';
import EfdVoiceDialog from './EfdVoiceDialog';
import ChartCard from './ChartCard';
import ChartSkeleton from './ChartSkeleton';
import GraphsPanel from './GraphsPanel';
import { useGraphGroups } from './graphGroups';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  chatBubbleSx,
  chatRowSx,
  BotAvatar,
  ModalHeader,
  InfoTooltip,
  modalHeaderBtnSx,
  firstLineSlotSx,
  FIRST_LINE_SLOT_PX,
  ScrollToBottomButton,
  EmailDraftCard,
  AttachmentCard,
} from './chatMessageStyles';
import useScrollToBottom from './hooks/useScrollToBottom';

/**
 * SQL data agent chat — Assistant Bro MH, PPI and EFD Survista share it; the
 * `agent` prop (MH_AGENT / PPI_AGENT / EFD_SURVISTA_AGENT below) selects
 * endpoints, keys and texts.
 *
 * Talks to POST <apiBase>/chat: the agent answers questions by querying its
 * database itself and streams the reply as SSE `text`
 * events (one final `done` event carries stats + tool log). The endpoint is
 * stateless — the full history rides along with every request.
 *
 * Message rendering deliberately reuses chatMessageStyles (the shared module
 * behind Assistant Lion / ARE / ChatWidget) so text, lists and tables look
 * identical across all chat surfaces. Speech-to-text input reuses the shared
 * useSpeechToText hook (same mic flow as ARE / ChatWidget).
 */
// One config per data agent — the component itself is agent-agnostic. The
// backend mirrors this with a profile per data source (src/data_agent/).
//   key          expandable id, VIP agent key, localStorage session prefix
//   apiBase      the chat / welcome / voice endpoints live under it
//   sttField     name of the mic field in the shared speech-to-text hook
//   *Key         i18n keys for title, intro line and input placeholder
export const MH_AGENT = {
  key: 'efd_agent',
  apiBase: '/api/efd-agent',
  sttField: 'efd-agent',
  titleKey: 'efd_agent',
  introKey: 'efd_agent_intro',
  placeholderKey: 'efd_agent_placeholder',
  Icon: QueryStatsIcon,
  // Session key from before sessions were per user; dropped on first load.
  legacyStorageKey: 'efd_agent_session',
};
export const PPI_AGENT = {
  key: 'ppi_agent',
  apiBase: '/api/data-agent/ppi',
  sttField: 'ppi-agent',
  titleKey: 'ppi_agent',
  introKey: 'ppi_agent_intro',
  placeholderKey: 'ppi_agent_placeholder',
  Icon: DonutSmallIcon,
};
export const EFD_SURVISTA_AGENT = {
  key: 'efd_survista_agent',
  apiBase: '/api/data-agent/efd_survista',
  sttField: 'efd-survista-agent',
  titleKey: 'efd_survista_agent',
  introKey: 'efd_survista_agent_intro',
  placeholderKey: 'efd_survista_agent_placeholder',
  Icon: AccountBalanceIcon,
};

/** The EFD assistant's chat for a data set variant (src/data_agent/assistants.py):
 *  one tile "Assistant Bro EFD"; the variant only picks the backend and the
 *  per-variant storage (sessions, saved prompts, graphs keep their keys). */
export function efdAgentFor(variant) {
  if (variant !== 'efd_survista') return MH_AGENT;
  return {
    ...EFD_SURVISTA_AGENT, titleKey: 'efd_agent', introKey: 'efd_agent_intro', placeholderKey: 'efd_agent_placeholder',
  };
}

// The chat lives across open/close cycles: the modal unmounts this component,
// so the session is kept in localStorage. "Clear chat" wipes it, which makes
// the next open a fresh session again (→ welcome snapshot).
//
// The key is PER USER: the agent's answers are scoped to the logged-in user
// (an advisor sees only their own book), so a session restored for a
// different login would show figures that user must not see.
// Saved prompts (VIPs) are stored per assistant under `agent.key`; 'chat'
// (Assistant Bro) stays untouched — see src/model/vip.py.
const storageKey = (agent) => {
  const user = getUser();
  const id = user && (user.id ?? user.username);
  return id != null ? `${agent.key}_session:${id}` : `${agent.key}_session:anonymous`;
};

const loadSession = (agent) => {
  try {
    if (agent.legacyStorageKey) localStorage.removeItem(agent.legacyStorageKey);
    const saved = JSON.parse(localStorage.getItem(storageKey(agent)));
    return Array.isArray(saved) ? saved.filter(m => m && m.role && m.content) : [];
  } catch (e) {
    return [];
  }
};

/**
 * An e-mail draft as a chat message. `content` is a one-line summary so the
 * draft is visible to the model in later turns (history) and in the Word
 * export; the UI renders the card from `emailDraft` instead of the text.
 */
const emailDraftMessage = (draft, t) => ({
  role: 'assistant',
  content: t('efd_email_draft_summary', {
    defaultValue: 'E-Mail-Entwurf an {{name}}: {{subject}}',
    name: draft.recipient_name || draft.recipient_email || '?',
    subject: draft.subject || '',
  }),
  emailDraft: draft,
});

/**
 * A file a tool produced (today: the theScreener PDF of a security) as a chat
 * message. Like the e-mail draft, `content` is a one-line summary so the file
 * stays visible to the model in later turns and in the Word export, while the
 * UI renders the card from `attachment`.
 */
const attachmentMessage = (attachment, t) => ({
  role: 'assistant',
  content: t('efd_attachment_summary', {
    defaultValue: 'Dokument bereitgestellt: {{title}}',
    title: attachment.title || attachment.filename || '',
  }),
  attachment,
});

/**
 * A chart the agent drew (create_chart tool) as a chat message. `content` is
 * the backend's compact text form of the chart — title, categories and the
 * exact values — so a follow-up like "now as a line chart" can redraw it from
 * the history, and the Word export has the figures. The UI renders the card
 * from `chart.spec`, which also survives a reload via localStorage.
 */
export const chartMessage = (data) => ({
  role: 'assistant',
  content: data.summary || data.spec?.title || '',
  chart: { id: data.id, spec: data.spec },
});

/** Any message the UI renders as a card instead of a markdown bubble. */
const isCard = (m) => !!(m && (m.emailDraft || m.attachment || m.chart || m.chartPending));

/** Read an SSE response body and hand each complete event to `onEvent`. */
async function readSseStream(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handle = (rawEvent) => {
    let eventType = '';
    let eventData = '';
    for (const line of rawEvent.split('\n')) {
      if (line.startsWith('event: ')) eventType = line.slice(7).trim();
      else if (line.startsWith('data: ')) eventData = line.slice(6);
    }
    if (!eventType || !eventData) return;
    try {
      onEvent(eventType, JSON.parse(eventData));
    } catch (e) {
      console.warn('Failed to parse SSE data:', eventData, e);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      buffer.split('\n\n').forEach(ev => ev.trim() && handle(ev));
      return;
    }
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop();          // keep the trailing partial event
    events.forEach(ev => ev.trim() && handle(ev));
  }
}

function EfdAgentChat({ onClose, language, userName = '', agent = MH_AGENT }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('chat');            // 'chat' | 'vip' | 'graphs'
  const [vips, setVips] = useState([]);
  const [graphs, setGraphs] = useState([]);           // starred charts (Graphs tab)
  const graphGroups = useGraphGroups(agent.key);      // their groups ("Max Müller", …)
  const [vipDialog, setVipDialog] = useState({ open: false, text: '', vip: null });
  const [vipDeleteDialog, setVipDeleteDialog] = useState({ open: false, vip: null });
  const [vipSaving, setVipSaving] = useState(false);
  const [messages, setMessages] = useState(() => loadSession(agent));   // {role: 'user'|'assistant', content}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // null = no welcome running; otherwise the backend's current stage.
  const [welcomeStage, setWelcomeStage] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const chatScrollRef = useRef(null);
  const abortRef = useRef(null);
  const welcomeStartedRef = useRef(false);
  const inputRef = useRef(null);

  // Put the cursor back into the input once a turn is over, so the next
  // question can be typed straight away. Deferred by a tick because the field
  // is disabled while `busy` — it only becomes focusable after the re-render.
  // preventScroll keeps the chat where it is: focusing would otherwise pull
  // the input into view and undo the "user message at the top" positioning.
  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  }, []);

  // Chat scroll behaviour: user message slides to the top on send, reply
  // streams below, "scroll to latest" arrow when not at the bottom (same hook
  // as Assistant Lion / ARE — see useScrollToBottom).
  const {
    showScrollDown, bottomSpacerRef, messagesEndRef, handleScroll,
    scrollToBottom, markUserSent, syncOnContentChange,
  } = useScrollToBottom(chatScrollRef);

  // ── Speech-to-text: record mic audio, transcribe, drop text into input ──
  const { recordingField, transcribingField, toggle: toggleRecording } = useSpeechToText({
    onResult: (_field, text) => setInput((prev) => (prev ? `${prev} ${text}` : text)),
  });
  const isRecording = recordingField === agent.sttField;
  const isTranscribing = transcribingField === agent.sttField;

  // Position the just-sent user message at the top of the viewport, then keep
  // the spacer sized as the reply streams in (no auto-follow). Skipped while
  // the voice overlay is open: spoken turns keep appending to the chat behind
  // it, and scrolling that hidden list only makes the overlay jump.
  useLayoutEffect(() => {
    if (voiceOpen) return;
    syncOnContentChange();
  }, [messages, busy, welcomeStage, voiceOpen, syncOnContentChange]);

  // Opening the modal on a restored session starts at the latest message.
  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist after every change, including mid-stream: closing the modal while
  // an answer is still coming keeps what had already arrived.
  useEffect(() => {
    try {
      if (messages.length) localStorage.setItem(storageKey(agent), JSON.stringify(messages));
      else localStorage.removeItem(storageKey(agent));
    } catch (e) { /* private mode / quota — the chat still works in memory */ }
  }, [messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // ── Welcome snapshot: only for a fresh session (nothing restored) ──
  const runWelcome = useCallback(async () => {
    setWelcomeStage('connecting');
    const abortController = new AbortController();
    abortRef.current = abortController;
    let started = false;

    const appendToWelcome = (chunk) => {
      setMessages(m => {
        if (!started) {
          started = true;
          return [...m, { role: 'assistant', content: chunk, welcome: true }];
        }
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last, content: last.content + chunk };
        return next;
      });
    };

    try {
      const res = await fetch(
        `${API_BASE_URL}${agent.apiBase}/welcome?language=${encodeURIComponent(language || 'de')}`,
        { headers: getAuthHeaders(), signal: abortController.signal },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      await readSseStream(res, (eventType, data) => {
        if (eventType === 'stage') setWelcomeStage(data.stage);
        else if (eventType === 'text' && data.text) {
          setWelcomeStage(null);          // first token → swap loader for content
          appendToWelcome(data.text);
        } else if (eventType === 'error') {
          setWelcomeStage(null);
          appendToWelcome(t('efd_agent_welcome_error'));
        }
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('EFD welcome failed:', e);
        setWelcomeStage(null);
        if (!started) appendToWelcome(t('efd_agent_welcome_error'));
      }
    } finally {
      setWelcomeStage(null);
      abortRef.current = null;
    }
  }, [language, t]);

  useEffect(() => {
    if (welcomeStartedRef.current) return;   // also guards StrictMode double-mount
    welcomeStartedRef.current = true;
    if (messages.length === 0) runWelcome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Very Important Prompts (scoped to this agent) ──
  const fetchVips = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/vip?agent=${agent.key}&user_name=${encodeURIComponent(userName)}`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setVips(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load prompts:', e);
    }
  }, [userName]);

  useEffect(() => { fetchVips(); }, [fetchVips]);

  const saveVip = async () => {
    const prompt = vipDialog.text.trim();
    if (!prompt) return;
    setVipSaving(true);
    try {
      const editing = vipDialog.vip;
      const res = await fetch(
        editing ? `${API_BASE_URL}/api/vip/${editing.id}` : `${API_BASE_URL}/api/vip`,
        {
          method: editing ? 'PUT' : 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(editing ? { prompt } : { user_name: userName, prompt, agent: agent.key }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setVipDialog({ open: false, text: '', vip: null });
      fetchVips();
    } catch (e) {
      console.error('Failed to save prompt:', e);
    } finally {
      setVipSaving(false);
    }
  };

  const deleteVip = async () => {
    const vip = vipDeleteDialog.vip;
    if (!vip) return;
    setVipSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vip/${vip.id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setVipDeleteDialog({ open: false, vip: null });
      fetchVips();
    } catch (e) {
      console.error('Failed to delete prompt:', e);
    } finally {
      setVipSaving(false);
    }
  };

  // ── Graphs: charts starred in this chat, kept per user and assistant ──
  // (backend /api/graphs; the user comes from the token, not from here)
  const fetchGraphs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/graphs?agent=${agent.key}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGraphs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load graphs:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchGraphs(); }, [fetchGraphs]);

  const deleteGraph = async (graph) => {
    const res = await fetch(`${API_BASE_URL}/api/graphs/${graph.id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
    setGraphs(g => g.filter(x => x.id !== graph.id));
    graphGroups.forgetGraphs([graph.id]);
  };

  /** "Ausgewählte löschen" in Graphs: one request for all of them. */
  const deleteGraphs = async (ids) => {
    const res = await fetch(`${API_BASE_URL}/api/graphs/delete`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ agent: agent.key, ids }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    setGraphs(g => g.filter(x => !ids.includes(x.id)));
    graphGroups.forgetGraphs(ids);
  };

  /** How a saved graph is shown (Grafik / Tabelle / beides) — also on its slide. */
  const setGraphView = async (graph, view) => {
    setGraphs(g => g.map(x => (x.id === graph.id ? { ...x, view } : x)));
    try {
      const res = await fetch(`${API_BASE_URL}/api/graphs/${graph.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ view }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      console.error('Failed to change the graph view:', e);
    }
  };

  /** The view switch on a chat card: remembered on the message (so the star
   *  saves what is on screen) and, once starred, on the saved graph too. */
  const setChartView = (chart, view) => {
    setMessages(m => m.map(x => (x.chart && x.chart.id === chart.id
      ? { ...x, chart: { ...x.chart, view } } : x)));
    const saved = graphs.find(g => g.chart_id === chart.id);
    if (saved && saved.view !== view) setGraphView(saved, view);
  };

  /** The star on a chart card: save it to Graphs, or take it out again. */
  const toggleStar = async (chart) => {
    const saved = graphs.find(g => g.chart_id === chart.id);
    try {
      if (saved) {
        await deleteGraph(saved);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/graphs`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          agent: agent.key, chart_id: chart.id, spec: chart.spec, view: chart.view || 'chart',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const graph = await res.json();
      setGraphs(g => [graph, ...g.filter(x => x.id !== graph.id)]);
    } catch (e) {
      console.error('Failed to star the chart:', e);
    }
  };

  /** "Ask again" from Graphs: the saved prompt goes into the input. */
  const askGraph = (prompt) => {
    setTab('chat');
    setInput(prompt);
    focusInput();
  };

  /** Clicking a saved prompt asks it straight away. */
  const askVip = (prompt) => {
    setTab('chat');
    setInput(prompt);
    focusInput();
  };

  /**
   * Download the conversation as a Word file — same endpoint and payload shape
   * as Assistant Bro (`{sender, text}` per message, 'user' rows get a border).
   * The opening snapshot is left out: it is a UI greeting, not part of the
   * exchange the advisor wants on paper.
   */
  const exportWord = async () => {
    // A chart goes along as its spec; the backend draws it as a picture.
    const payload = messages
      .filter(m => !m.welcome && m.content)
      .map(m => ({
        sender: m.role === 'user' ? 'user' : 'bot',
        text: m.content,
        ...(m.chart ? { chart: m.chart.spec } : {}),
      }));
    if (!payload.length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/generate_word_document`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ messages: payload }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ChatHistory.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Word export failed:', e);
    }
  };

  // The conversation this chat belongs to (Activity view): carried by its
  // first question; a new one starts with the first question of an empty chat.
  const pendingConversation = useRef(null);
  const conversationId = () => {
    const first = messages.find(m => m.role === 'user');
    if (first) return first.cid;
    if (!pendingConversation.current) pendingConversation.current = newConversationId();
    return pendingConversation.current;
  };

  const clearChat = () => {
    abortRef.current?.abort();
    pendingConversation.current = null;
    setMessages([]);
    setInput('');
    setBusy(false);
    try { localStorage.removeItem(storageKey(agent)); } catch (e) { /* ignore */ }
    runWelcome();   // an emptied chat is a fresh session → snapshot again
  };

  const send = async () => {
    const question = input.trim();
    if (!question || busy || welcomeStage) return;
    setInput('');
    setBusy(true);
    markUserSent();   // slide this user message up to the top; reply streams below

    // History = everything before this question (the endpoint is stateless).
    const history = messages.map(({ role, content }) => ({ role, content }));
    const cid = conversationId();
    setMessages(m => [...m, { role: 'user', content: question, cid }, { role: 'assistant', content: '' }]);

    const abortController = new AbortController();
    abortRef.current = abortController;

    const appendToAnswer = (chunk) => {
      setMessages(m => {
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last, content: last.content + chunk };
        return next;
      });
    };

    try {
      const res = await fetch(`${API_BASE_URL}${agent.apiBase}/chat`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        // The answer follows the language of `question` (detected server-side);
        // the UI language only applies when the question has none of its own.
        body: JSON.stringify({ message: question, history, language, conversation_id: cid }),
        signal: abortController.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Cards (e-mail draft, attachment) arrive while the answer bubble is
      // still empty (tool turn): slot the card in before it and keep the
      // bubble for the model's one-line confirmation that follows.
      const withCard = (m, card) => {
        const next = [...m];
        const last = next[next.length - 1];
        const pending = last && last.role === 'assistant' && !isCard(last) ? next.pop() : null;
        next.push(card);
        next.push(pending || { role: 'assistant', content: '' });
        return next;
      };
      const insertCard = (card) => setMessages(m => withCard(m, card));

      // Chart skeleton: shown as soon as the model starts writing a
      // create_chart call; the finished chart takes its place. One skeleton
      // at a time — a retried call (rejected spec) reuses it.
      const showChartPending = () => setMessages(m => (m.some(x => x.chartPending) ? m
        : withCard(m, { role: 'assistant', content: '', chartPending: true })));
      const placeChart = (card) => setMessages(m => {
        const index = m.findIndex(x => x.chartPending);
        if (index < 0) return withCard(m, card);
        const next = [...m];
        next[index] = card;
        return next;
      });

      await readSseStream(res, (eventType, data) => {
        if (eventType === 'text' && data.text) appendToAnswer(data.text);
        else if (eventType === 'email_draft' && data) insertCard(emailDraftMessage(data, t));
        else if (eventType === 'attachment' && data && data.url) insertCard(attachmentMessage(data, t));
        else if (eventType === 'chart_pending') showChartPending();
        else if (eventType === 'chart' && data && data.spec) placeChart(chartMessage(data));
        else if (eventType === 'error') appendToAnswer(`\n\n_${data.message || 'Fehler'}_`);
        else if (eventType === 'done') console.log('EFD agent done:', data.stats, data.tool_log);
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('EFD agent request failed:', e);
        appendToAnswer(`\n\n_${t('efd_agent_error')}_`);
      }
    } finally {
      // A skeleton whose chart never came (rejected call, error, abort) goes.
      setMessages(m => (m.some(x => x.chartPending) ? m.filter(x => !x.chartPending) : m));
      setBusy(false);
      abortRef.current = null;
      focusInput();   // answer complete → ready for the next question
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    // NOTE: deliberately NOT `position: relative`. The voice overlay is
    // `absolute; inset: 0` and must attach to the modal wrapper in App.js
    // (which spans the whole rounded paper), not to this box — that box sits
    // inside the modal's padding, which would leave a visible frame around
    // the overlay. Same arrangement as the ARE voice dialog.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ModalHeader
        icon={<agent.Icon />}
        title={t(agent.titleKey)}
        onClose={onClose}
        sx={{ pb: 1 }}
        extra={tab === 'chat' && (
          <InfoTooltip title={messages.length === 0 ? t('no_chat_history') : ''}>
            <span>
              <Button
                onClick={clearChat}
                disabled={isRecording || isTranscribing || !!welcomeStage || messages.length === 0}
                size="small"
                startIcon={<DeleteOutlineIcon />}
                sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}
              >
                {t('clear_chat')}
              </Button>
            </span>
          </InfoTooltip>
        )}
      />

      {/* ─── Tabs (same styling as Assistant Bro) ─── */}
      <Tabs
        value={['chat', 'vip', 'graphs'].indexOf(tab)}
        onChange={(e, v) => setTab(['chat', 'vip', 'graphs'][v])}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid rgba(25, 208, 239, 0.35)',
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontSize: 'var(--el-font-size-button)',
            color: 'text.secondary',
            '&.Mui-selected': { color: 'primary.main' },
          },
        }}
      >
        <Tab label={t('chat-tab')} />
        <Tab label={t('very-important-prompts')} />
        <Tab label={t('graphs_tab', { defaultValue: 'Graphs' })} />
      </Tabs>

      {/* ─── Starred charts → PowerPoint ─── */}
      {tab === 'graphs' && (
        <GraphsPanel
          graphs={graphs}
          groups={graphGroups}
          agentKey={agent.key}
          language={language}
          onDelete={deleteGraph}
          onDeleteMany={deleteGraphs}
          onViewChange={setGraphView}
          onAsk={askGraph}
        />
      )}

      {/* ─── Saved prompts ─── */}
      {tab === 'vip' && (
        <Box sx={{ pt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Button
            onClick={() => setVipDialog({ open: true, text: '', vip: null })}
            size="small"
            startIcon={<AddIcon />}
            sx={{ ...modalHeaderBtnSx, alignSelf: 'flex-start', mb: 1.5, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}
          >
            {t('efd_vip_add')}
          </Button>
          {vips.length > 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
              {vips.map((vip, index) => (
                <Box
                  key={vip.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1.5, mb: 1,
                    borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.06)',
                    border: 1, borderColor: 'divider',
                  }}
                >
                  <Box onClick={() => askVip(vip.prompt)} sx={{ flex: 1, cursor: 'pointer' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      #{index + 1}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {vip.prompt}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setVipDialog({ open: true, text: vip.prompt, vip })}
                    size="small"
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    onClick={() => setVipDeleteDialog({ open: true, vip })}
                    size="small"
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('efd_vip_empty')}
            </Typography>
          )}
        </Box>
      )}

      {/* ─── Messages ─── */}
      {/* Row spacing (mt/pr on the list, mb: 1 per row) is taken from
          Assistant Lion so all chat surfaces breathe the same. The relative
          wrapper is what the floating "scroll to latest" arrow pins to. */}
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: tab === 'chat' ? 'flex' : 'none', flexDirection: 'column' }}>
      <Box
        ref={chatScrollRef}
        onScroll={handleScroll}
        sx={{ position: 'relative', flex: 1, overflowY: 'auto', mt: '14px', pr: 1.5 }}
      >
        {messages.length === 0 && !welcomeStage && (
          <Typography sx={{ color: 'text.secondary', fontSize: '14px', textAlign: 'center', mt: 4, px: 4 }}>
            {t(agent.introKey)}
          </Typography>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          // A chart (or its skeleton while it is being built) takes the full
          // row width next to the avatar — a plot needs the room a bubble
          // would not give it.
          if (m.chart || m.chartPending) {
            return (
              <Box key={i} sx={{ ...chatRowSx(false), mb: 1 }}>
                <BotAvatar />
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: 'calc(100% - 40px)' }}>
                  {m.chart ? (
                    <ChartCard
                      spec={m.chart.spec}
                      view={m.chart.view || 'chart'}
                      onViewChange={(view) => setChartView(m.chart, view)}
                      extra={(() => {
                        const starred = graphs.some(g => g.chart_id === m.chart.id);
                        return (
                          <InfoTooltip title={starred
                            ? t('graphs_star_remove', { defaultValue: 'Aus Graphs entfernen' })
                            : t('graphs_star_add', { defaultValue: 'In Graphs speichern' })}>
                            <IconButton size="small" onClick={() => toggleStar(m.chart)}
                              aria-pressed={starred}
                              sx={{ color: starred ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                              {starred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </InfoTooltip>
                        );
                      })()}
                    />
                  ) : <ChartSkeleton />}
                </Box>
              </Box>
            );
          }
          // While the answer is still empty, the bubble is replaced by a
          // transparent box exactly one avatar-slot tall, so the spinner sits
          // centred next to the avatar instead of hanging at the top.
          const isLoadingBubble = !isUser && !m.content && !isCard(m);
          // The Word button only makes sense on a finished answer: not on the
          // opening snapshot, not on a card (e-mail draft, attachment, chart),
          // and not while this very message is still coming in.
          const showWordExport = !isUser && !!m.content && !m.welcome && !isCard(m)
            && !(busy && i === messages.length - 1);
          return (
            <Box key={i} className={isUser ? 'chat-user-msg' : undefined} sx={{ ...chatRowSx(isUser), mb: 1 }}>
              {!isUser && <BotAvatar />}
              <Box
                sx={isLoadingBubble
                  ? {
                      px: 0.5,
                      bgcolor: 'transparent',
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: `${FIRST_LINE_SLOT_PX}px`,
                    }
                  : { ...chatBubbleSx(isUser), whiteSpace: isUser ? 'pre-wrap' : 'normal' }}
              >
                {isUser
                  ? m.content
                  : m.emailDraft
                    // Same card as Assistant Bro: recipient, subject, body,
                    // "E-Mail öffnen" (mailto). Nothing is sent from the app.
                    ? <EmailDraftCard draft={m.emailDraft} openLabel={t('open_email')} />
                    : m.attachment
                    // A file a tool produced — today the theScreener PDF of a
                    // security. The link only ever lives on this button.
                    ? <AttachmentCard attachment={m.attachment} openLabel={t('efd_attachment_open')} />
                    : (m.content
                      // alignNumericColumns right-aligns figure columns, so
                      // amounts and percentages line up like in a report.
                      ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>{alignNumericColumns(m.content)}</ReactMarkdown>
                      : <CircularProgress size={22} thickness={4} sx={{ color: 'primary.main' }} />)}
                {/* Export the exchange as Word — same button, position and
                    endpoint as Assistant Bro; it always exports the whole
                    conversation, not just this message. */}
                {showWordExport && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <InfoTooltip title={t('export_chat_history_to_word')}>
                      <IconButton size="small" onClick={exportWord} sx={{ color: 'text.secondary' }}>
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                )}
              </Box>
              {/* Keep a question you'll ask again as a saved prompt. The
                  firstLineSlotSx wrapper is what centres the star on the
                  bubble's first line — same slot the bot avatar sits in, so
                  both sides of the conversation share one baseline. Styling is
                  taken 1:1 from Assistant Bro. */}
              {isUser && (
                <Box sx={firstLineSlotSx}>
                  <InfoTooltip title={t('efd_vip_save')}>
                    <IconButton
                      onClick={() => setVipDialog({ open: true, text: m.content, vip: null })}
                      size="small"
                      sx={{ color: 'primary.main', p: 0.5 }}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </InfoTooltip>
                </Box>
              )}
            </Box>
          );
        })}

        {/* Opening snapshot being built: real backend stages + a skeleton of
            the message that is about to arrive. */}
        {welcomeStage && (
          <Box sx={chatRowSx(false)}>
            <BotAvatar />
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 'calc(100% - 40px)' }}>
              <EfdWelcomeLoader stage={welcomeStage} />
            </Box>
          </Box>
        )}

        {/* Growable spacer + scroll anchor: let the just-sent user message be
            scrolled to the top of the viewport. */}
        <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
        <div ref={messagesEndRef} />
      </Box>
        {/* "Scroll to latest" arrow — shown when not at the bottom. */}
        <ScrollToBottomButton show={showScrollDown} onClick={scrollToBottom} />
      </Box>

      {/* ─── Input ─── */}
      <Box sx={{ display: tab === 'chat' ? 'flex' : 'none', alignItems: 'center', gap: 0.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={5}
          size="small"
          variant="outlined"
          placeholder={isTranscribing ? '' : t(agent.placeholderKey)}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy || isTranscribing}
          InputProps={{
            startAdornment: isTranscribing ? (
              <InputAdornment position="start">
                <CircularProgress size={18} sx={{ color: 'primary.main' }} />
              </InputAdornment>
            ) : null,
          }}
        />
        <InfoTooltip title={t('send_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              color="primary"
              onClick={send}
              disabled={busy || isRecording || isTranscribing || !input.trim()}
              aria-label="send"
            >
              {busy ? <CircularProgress size={22} /> : <SendRoundedIcon />}
            </IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={isRecording ? t('stt_stop_tooltip') : t('stt_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              onClick={() => toggleRecording(agent.sttField)}
              disabled={busy || isTranscribing}
              aria-label="Speech to text"
              sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}
            >
              {isRecording ? <FiberManualRecordIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={t('efd_voice_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              onClick={() => setVoiceOpen(true)}
              disabled={busy || isRecording || isTranscribing || !!welcomeStage}
              aria-label="Voice conversation"
              sx={{ color: 'text.secondary' }}
            >
              {/* Same waveform glyph as the ARE voice button. */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </IconButton>
          </span>
        </InfoTooltip>
      </Box>

      {/* ─── Speech-to-speech overlay ─── */}
      {voiceOpen && (
        <EfdVoiceDialog
          apiBase={agent.apiBase}
          language={language}
          history={messages.map(({ role, content }) => ({ role, content }))}
          conversationId={conversationId()}
          onTurn={({ question, answer, emailDrafts = [], attachments = [], charts = [] }) => {
            // Spoken turns join the written conversation, so closing the
            // overlay leaves the whole exchange in the chat — e-mail drafts,
            // files and charts included, as the same cards the text chat shows.
            setMessages(m => [
              ...m,
              ...(question ? [{ role: 'user', content: question, cid: conversationId() }] : []),
              ...emailDrafts.map(d => emailDraftMessage(d, t)),
              ...attachments.map(a => attachmentMessage(a, t)),
              ...charts.map(c => chartMessage(c)),
              ...(answer ? [{ role: 'assistant', content: answer }] : []),
            ]);
          }}
          onClose={() => setVoiceOpen(false)}
        />
      )}

      {/* ─── Save / edit a prompt ─── */}
      <Dialog open={vipDialog.open} onClose={() => !vipSaving && setVipDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
          {vipDialog.vip ? t('efd_vip_edit_title') : t('efd_vip_add_title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            value={vipDialog.text}
            onChange={(e) => setVipDialog(d => ({ ...d, text: e.target.value }))}
            label={t('vip_prompt_label', { defaultValue: 'Prompt' })}
            multiline minRows={2} maxRows={6} fullWidth autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVipDialog(d => ({ ...d, open: false }))} disabled={vipSaving}>
            {t('cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button onClick={saveVip} variant="contained" disabled={vipSaving || !vipDialog.text.trim()}>
            {vipSaving ? <CircularProgress size={18} /> : t('save', { defaultValue: 'Speichern' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete confirmation ─── */}
      <Dialog open={vipDeleteDialog.open} onClose={() => !vipSaving && setVipDeleteDialog({ open: false, vip: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>{t('efd_vip_delete_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {vipDeleteDialog.vip?.prompt}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVipDeleteDialog({ open: false, vip: null })} disabled={vipSaving}>
            {t('cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button onClick={deleteVip} color="error" variant="contained" disabled={vipSaving}>
            {vipSaving ? <CircularProgress size={18} /> : t('delete', { defaultValue: 'Löschen' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EfdAgentChat;
